import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';

interface MachineIframeProps {
  machineId: string;
  indusoft_project?: string;
  vueAppUrl?: string;
  width?: string | number;
  height?: string | number;
}

// Helper function to build Vue app URL
const buildVueAppUrl = (indusoft_project?: string): string => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const vueAppUrlOverride = process.env.REACT_APP_VUE_APP_URL_OVERRIDE;

  // If override URL is provided, use it (bypasses all dynamic building)
  if (vueAppUrlOverride) {
    return vueAppUrlOverride;
  }

  // In development, use localhost if no override is set
  if (isDevelopment) {
    return 'http://localhost:3001';
  }

  // Production: build URL based on indusoft_project
  if (indusoft_project) {
    return `https://autobulk.agconnex.com/latest/index.html`;
  }

  // Fallback
  return 'http://localhost:3001';
};

export const MachineIframe: React.FC<MachineIframeProps> = ({
  machineId,
  indusoft_project,
  vueAppUrl,
  width = '100%',
  height = '600px',
}) => {
  // Build the Vue app URL dynamically if not explicitly provided
  const finalVueAppUrl = vueAppUrl || buildVueAppUrl(indusoft_project);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Wait for iframe to load, then send auth tokens via postMessage
    const handleLoad = () => {
      console.log('🔧 Iframe loaded, sending auth setup via postMessage for machine:', machineId);

      // Get React's auth tokens
      const reactBearerToken = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');

      console.log('🔑 React bearer token found:', !!reactBearerToken);

      // Send auth setup via postMessage to avoid CORS issues
      setTimeout(() => {
        // Build API base URL dynamically based on environment
        const isDevelopment = process.env.NODE_ENV === 'development';
        const apiBaseUrl = isDevelopment
          ? 'http://localhost:8000'
          : process.env.REACT_APP_API_BASE_URL || window.location.origin;

        const authData = {
          type: 'AGCONNEX_AUTH_SETUP',
          payload: {
            agconnex_bearer_token: reactBearerToken || 'test-token-123',
            api_base_url: `${apiBaseUrl}/api/remote_proxy/machine/${machineId}`,
            agconnex_machine_id: machineId,
          },
        };

        console.log('📨 Sending auth setup via postMessage:', authData);

        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage(authData, '*');
        }
      }, 2000); // Wait for Vue app to initialize
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [machineId]);

  return (
    <Box sx={{ width, height, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <iframe
        ref={iframeRef}
        src={finalVueAppUrl}
        data-machine-id={machineId}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: 'inherit',
        }}
        title={`Machine ${machineId} Interface`}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    </Box>
  );
};
